import React, { useState, useEffect } from "react";
import { Plus, GripVertical, Pencil, Trash2 } from "lucide-react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckinSettingsResponse } from "@/types/booking-checkin-settings";
import { CheckinQuestion } from "@/types/checkin-question";

interface CustomQuestionsTabProps {
  settings: CheckinSettingsResponse | null;
  setSettings: React.Dispatch<
    React.SetStateAction<CheckinSettingsResponse | null>
  >;
}

interface QuestionItemProps {
  question: CheckinQuestion;
  onEdit: (index: number, text: string) => void;
  onDelete: (index: number) => void;
  index: number;
}

const QuestionItem: React.FC<QuestionItemProps> = ({
  question,
  onEdit,
  onDelete,
  index,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(question.question);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id || `temp-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSave = () => {
    if (editText.trim()) {
      onEdit(index, editText);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditText(question.question);
    setIsEditing(false);
  };

  useEffect(() => {
    setEditText(question.question);
  }, [question.question]);

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 p-2">
      <div
        {...attributes}
        {...listeners}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="h-5 w-5 text-gray-400" />
      </div>

      {isEditing ? (
        <>
          <Input
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="flex-1"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") handleCancel();
            }}
          />
          <Button variant="new_default" size="sm" onClick={handleSave}>
            Save
          </Button>
          <Button size="sm" variant="new_outline" onClick={handleCancel}>
            Cancel
          </Button>
        </>
      ) : (
        <>
          <Input
            value={question.question}
            readOnly
            className="flex-1 cursor-default"
            onClick={() => setIsEditing(true)}
          />
          <Button
            size="icon_sm"
            variant="new_ghost"
            onClick={() => setIsEditing(true)}
            className="flex-shrink-0"
            aria-label="Edit question"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="icon_sm"
            variant="new_ghost"
            onClick={() => onDelete(index)}
            className="flex-shrink-0"
            aria-label="Delete question"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  );
};

export const CustomQuestionsTab: React.FC<CustomQuestionsTabProps> = ({
  settings,
  setSettings,
}) => {
  const [localQuestions, setLocalQuestions] = useState<CheckinQuestion[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Initialize local questions from settings
  useEffect(() => {
    setLocalQuestions(settings?.questions || []);
  }, [settings?.questions]);

  const updateQuestions = (newQuestions: CheckinQuestion[]) => {
    setLocalQuestions(newQuestions);
    setSettings((previousSettings) =>
      previousSettings
        ? {
            ...previousSettings,
            questions: newQuestions,
          }
        : previousSettings,
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localQuestions.findIndex(
        (q) => (q.id || `temp-${localQuestions.indexOf(q)}`) === active.id,
      );
      const newIndex = localQuestions.findIndex(
        (q) => (q.id || `temp-${localQuestions.indexOf(q)}`) === over.id,
      );

      const reordered = arrayMove(localQuestions, oldIndex, newIndex);
      // Update order property
      const withOrder = reordered.map((q, idx) => ({ ...q, order: idx }));
      updateQuestions(withOrder);
    }
  };

  const handleAdd = () => {
    const newQuestion: CheckinQuestion = {
      id: `temp-${Date.now()}`,
      question: "",
      order: localQuestions.length,
      checkinSettingsId: settings?.id || "",
    };
    updateQuestions([...localQuestions, newQuestion]);
  };

  const handleEdit = (index: number, text: string) => {
    const updated = localQuestions.map((q, i) =>
      i === index ? { ...q, question: text } : q,
    );
    updateQuestions(updated);
  };

  const handleDelete = (index: number) => {
    const updated = localQuestions.filter((_, i) => i !== index);
    updateQuestions(updated);
  };

  const toggleSetting = (key: keyof CheckinSettingsResponse) => {
    setSettings((previousSettings) =>
      previousSettings
        ? {
            ...previousSettings,
            [key]: !previousSettings[key],
          }
        : previousSettings,
    );
  };

  if (!settings) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Switch
            checked={settings.enabledCustomQuestions}
            onCheckedChange={() => toggleSetting("enabledCustomQuestions")}
          />
          <span className="text-lg font-normal">Custom questions</span>
        </div>
        {settings.enabledCustomQuestions && (
          <Button
            variant="new_outline"
            size="icon_sm"
            onClick={handleAdd}
            aria-label="Add question"
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </div>

      {settings.enabledCustomQuestions && (
        <div className="space-y-2 pl-8">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={localQuestions.map((q, i) => q.id || `temp-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {localQuestions.map((question, index) => (
                  <QuestionItem
                    key={question.id || `temp-${index}`}
                    question={question}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    index={index}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Switch
          checked={settings.skipCustomQuestions}
          onCheckedChange={() => toggleSetting("skipCustomQuestions")}
        />
        <span className="text-lg font-normal">Skip button</span>
      </div>
    </div>
  );
};
